import { NavLink } from 'react-router-dom'
import Icon from '../Icon'
import type { IconName } from '../Icon'

/**
 * The floating tab bar.
 *
 * Matches the iOS app rather than a flat web bottom bar: a raised pill that
 * sits above the content with the active tab in its own lighter capsule. The
 * five destinations are the iOS five — Today, Tours, Itinerary, Database,
 * Account — not the desktop sidebar's six. Ticketing is disabled in v1 and
 * Export belongs on a bigger screen, so neither earns a slot.
 */
const TABS: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: '/', label: 'Today', icon: 'sun', end: true },
  { to: '/tours', label: 'Tours', icon: 'mic' },
  { to: '/itinerary', label: 'Itinerary', icon: 'calendar' },
  { to: '/database', label: 'Database', icon: 'database' },
  { to: '/account', label: 'Account', icon: 'account' },
]

export default function TabBar() {
  return (
    <>
      {/* Content dissolves into the page behind the bar instead of stopping at
          a hard edge. Pinned to the bottom of the *dynamic* viewport so it
          tracks Safari's URL bar as that collapses, and non-interactive so it
          never eats a tap meant for the row underneath. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          height: 132,
          zIndex: 30,
          pointerEvents: 'none',
          background:
            'linear-gradient(to top, var(--bg) 0%, var(--bg) 34%, transparent 100%)',
        }}
      />
      <nav
        aria-label="Main"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          // Floats clear of the home indicator rather than sitting under it.
          bottom: 'calc(10px + env(safe-area-inset-bottom))',
          zIndex: 40,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))`,
          gap: 2,
          width: 'min(calc(100% - 24px), 430px)',
          padding: 6,
          borderRadius: 999,
          background: 'var(--panel)',
          border: 'var(--hairline-width) solid var(--hairline)',
          boxShadow: 'var(--shadow-pop)',
          pointerEvents: 'auto',
        }}
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            style={({ isActive }) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              // Comfortably over the 44pt minimum with the label included.
              minHeight: 52,
              borderRadius: 999,
              fontSize: '0.6rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? 'var(--accent-text)' : 'var(--text-secondary)',
              background: isActive ? 'var(--panel-raised)' : 'transparent',
            })}
          >
            {({ isActive }) => (
              <>
                <Icon name={tab.icon} size={20} strokeWidth={isActive ? 2 : 1.7} />
                <span>{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
      </nav>
    </>
  )
}

/**
 * Clearance every mobile screen needs under its content.
 *
 * Matches the fade height, so the last row scrolls fully clear of the bar
 * rather than resting half-dissolved behind it.
 */
export const TAB_BAR_INSET = 132
